import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { examenService } from '../services/api';
import './TomarExamen.css';

const TomarExamen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recuperacionId = searchParams.get('recuperacion');
  const esRecuperacion = Boolean(recuperacionId);

  const [examen, setExamen] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadExamen = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [examenResponse, preguntasResponse] = await Promise.all([
        examenService.getById(id, recuperacionId || undefined),
        examenService.preguntas(id, recuperacionId || undefined),
      ]);

      setExamen(examenResponse.data);

      let preguntasData = [];
      if (preguntasResponse?.data) {
        if (Array.isArray(preguntasResponse.data.preguntas)) {
          preguntasData = preguntasResponse.data.preguntas;
        } else if (Array.isArray(preguntasResponse.data)) {
          preguntasData = preguntasResponse.data;
        }
      }

      setPreguntas(preguntasData);

      const initialRespuestas = {};
      if (Array.isArray(preguntasData) && preguntasData.length > 0) {
        preguntasData.forEach((pregunta) => {
          if (pregunta && pregunta.id) {
            initialRespuestas[pregunta.id] = '';
          }
        });
      }
      setRespuestas(initialRespuestas);

      if (preguntasData.length === 0) {
        setError('No hay preguntas disponibles para este examen');
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || err.response?.data?.message || 'Error al cargar el examen';
      setError(errorMessage);
      setPreguntas([]);
      setRespuestas({});
      console.error('Error al cargar examen:', err);
    } finally {
      setLoading(false);
    }
  }, [id, recuperacionId]);

  useEffect(() => {
    loadExamen();
  }, [loadExamen]);

  const handleRespuestaChange = (preguntaId, respuesta) => {
    setRespuestas((prev) => ({
      ...prev,
      [preguntaId]: respuesta,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!Array.isArray(preguntas) || preguntas.length === 0) {
      setError('No hay preguntas para responder');
      return;
    }

    const preguntasSinRespuesta = preguntas.filter(
      (p) => p && p.id && (!respuestas[p.id] || respuestas[p.id].trim() === '')
    );

    if (preguntasSinRespuesta.length > 0) {
      if (
        !window.confirm(
          `Tienes ${preguntasSinRespuesta.length} pregunta(s) sin responder. ¿Deseas continuar?`
        )
      ) {
        return;
      }
    }

    setSubmitting(true);
    setError('');

    try {
      const respuestasArray = preguntas
        .filter((pregunta) => pregunta && pregunta.id)
        .map((pregunta) => ({
          pregunta_id: pregunta.id,
          respuesta: respuestas[pregunta.id] || '',
        }));

      await examenService.responder(id, respuestasArray, recuperacionId || undefined);
      navigate('/calificaciones', { state: { examenId: id } });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enviar el examen');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando examen...</div>;
  }

  if (error && !examen) {
    return <div className="error">{error}</div>;
  }

  if (!examen) {
    return <div className="error">Examen no encontrado</div>;
  }

  if (!preguntas || preguntas.length === 0) {
    return (
      <div className="tomar-examen">
        <h1>{examen.titulo}</h1>
        {esRecuperacion && <span className="badge warning">Examen de recuperación</span>}
        {error && <div className="error-message">{error}</div>}
        <div className="error">No hay preguntas disponibles para este examen</div>
      </div>
    );
  }

  return (
    <div className="tomar-examen">
      <h1>{examen.titulo}</h1>
      {esRecuperacion && (
        <p className="recuperacion-banner">🔄 Estás realizando un examen de recuperación</p>
      )}
      {examen.descripcion && <p className="descripcion">{examen.descripcion}</p>}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {preguntas.map((pregunta, index) => {
          if (!pregunta || !pregunta.id) return null;
          return (
            <div key={pregunta.id} className="pregunta-card">
              <h3>
                Pregunta {index + 1} ({pregunta.puntos} punto{pregunta.puntos !== 1 ? 's' : ''})
              </h3>
              <p className="pregunta-texto">{pregunta.pregunta_texto}</p>

              {pregunta.tipo_pregunta === 'opcion_multiple' && (
                <div className="opciones">
                  {pregunta.opcion_a && (
                    <label className="opcion">
                      <input
                        type="radio"
                        name={`pregunta-${pregunta.id}`}
                        value="a"
                        checked={respuestas[pregunta.id] === 'a'}
                        onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                      />
                      <span>A) {pregunta.opcion_a}</span>
                    </label>
                  )}
                  {pregunta.opcion_b && (
                    <label className="opcion">
                      <input
                        type="radio"
                        name={`pregunta-${pregunta.id}`}
                        value="b"
                        checked={respuestas[pregunta.id] === 'b'}
                        onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                      />
                      <span>B) {pregunta.opcion_b}</span>
                    </label>
                  )}
                  {pregunta.opcion_c && (
                    <label className="opcion">
                      <input
                        type="radio"
                        name={`pregunta-${pregunta.id}`}
                        value="c"
                        checked={respuestas[pregunta.id] === 'c'}
                        onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                      />
                      <span>C) {pregunta.opcion_c}</span>
                    </label>
                  )}
                  {pregunta.opcion_d && (
                    <label className="opcion">
                      <input
                        type="radio"
                        name={`pregunta-${pregunta.id}`}
                        value="d"
                        checked={respuestas[pregunta.id] === 'd'}
                        onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                      />
                      <span>D) {pregunta.opcion_d}</span>
                    </label>
                  )}
                </div>
              )}

              {pregunta.tipo_pregunta === 'verdadero_falso' && (
                <div className="opciones">
                  <label className="opcion">
                    <input
                      type="radio"
                      name={`pregunta-${pregunta.id}`}
                      value="verdadero"
                      checked={respuestas[pregunta.id] === 'verdadero'}
                      onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                    />
                    <span>Verdadero</span>
                  </label>
                  <label className="opcion">
                    <input
                      type="radio"
                      name={`pregunta-${pregunta.id}`}
                      value="falso"
                      checked={respuestas[pregunta.id] === 'falso'}
                      onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                    />
                    <span>Falso</span>
                  </label>
                </div>
              )}

              {pregunta.tipo_pregunta === 'texto' && (
                <textarea
                  className="respuesta-texto"
                  value={respuestas[pregunta.id] || ''}
                  onChange={(e) => handleRespuestaChange(pregunta.id, e.target.value)}
                  rows={4}
                  placeholder="Escribe tu respuesta aquí..."
                />
              )}
            </div>
          );
        })}

        <div className="examen-actions">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={submitting} className="btn-primary btn-examen-submit">
            {submitting ? 'Enviando...' : esRecuperacion ? 'Enviar Recuperación' : 'Enviar Examen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TomarExamen;
